import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Loader2, CheckCircle } from "lucide-react";
import { GLOBAL_IMAGES } from "../../assets/asset";
import axiosInstance from "../../api/axiosInstance";

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  
  const handleInputChange = (e) => {
    setEmail(e.target.value);
    setErrors({});
  };
  
  const handleResetPassword = async () => {
    if (!email) {
      setErrors({ email: "Email is required" });
      return;
    } else if (!validateEmail(email)) {
      setErrors({ email: "Please enter a valid email address" });
      return;
    }
    
    setLoading(true);
    
    try {
      await axiosInstance.post("api/sendlinkforgetpassword", { email, isWeb:"1" });
      setSuccess(true);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setErrors({ 
        submit: error.response?.data?.message || "Failed to send reset link. Please try again." 
      });
    }
  };
  
  return (
    <div
      className="flex flex-col items-center justify-center font-mali min-h-screen"
      style={{
        backgroundImage: `url(${GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute top-6 left-6">
        <img
          src={GLOBAL_IMAGES.dingo}
          alt="Mathdingo Logo"
          className="w-20 h-20"
        />
      </div>
      
      <div className="w-full max-w-xl px-4">
        <div className="bg-white/50 rounded-3xl p-8 shadow-lg">
          <div className="flex items-center gap-4 mb-8">
            <img
              src={GLOBAL_IMAGES.LoginBackButton}
              alt="Back"
              className="w-14 h-14 cursor-pointer"
              onClick={() => navigate(-1)}
            />
            <h2 className="text-4xl font-bold">Forgot Password</h2>
          </div>
          
          {!success ? (
            <div className="space-y-6">
              <p className="text-gray-700 mb-4">
                Enter your email address and we'll send you a link to reset your password.
              </p>
              
              <div>
                <label className="block text-xl font-bold text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-full border-2 border-gray-300 focus:outline-none focus:border-blue-500"
                  placeholder="Enter your email address"
                />
                {errors.email && (
                  <div className="flex items-center gap-2 mt-2 px-4 py-2 bg-red-50 border-2 border-red-300 rounded-full text-red-700 text-sm">
                    <AlertCircle size={16} />
                    <span>{errors.email}</span>
                  </div>
                )}
              </div>
              
              <button
                onClick={handleResetPassword}
                disabled={loading}
                className="w-full bg-blue-800 text-white py-3 rounded-full hover:bg-blue-900 transition-colors text-xl font-semibold flex items-center justify-center"
              >
                {loading ? (
                  <Loader2 className="animate-spin" size={24} />
                ) : (
                  "Send Reset Link"
                )}
              </button>
              
              <div className="text-center mt-4">
                <button
                  onClick={() => navigate("/login")}
                  className="text-blue-800 text-sm font-semibold hover:underline"
                >
                  Back to Login
                </button>
              </div>
              
              {errors.submit && (
                <div className="flex items-center gap-2 mt-2 px-4 py-2 bg-red-50 border-2 border-red-300 rounded-full text-red-700 text-sm">
                  <AlertCircle size={16} />
                  <span>{errors.submit}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6 text-center">
              <div className="flex justify-center">
                <CheckCircle size={64} className="text-green-500" />
              </div>
              
              <h3 className="text-2xl font-bold text-gray-800">
                Reset Link Sent!
              </h3>
              
              <p className="text-gray-700">
                We've sent a password reset link to <span className="font-bold">{email}</span>. 
                Please check your email and follow the instructions to reset your password.
              </p>
              
              <p className="text-gray-600 text-sm">
                If you don't see the email, check your spam folder or try again.
              </p>
              
              <div className="pt-4">
                <button
                  onClick={() => navigate("/login")}
                  className="w-full bg-blue-800 text-white py-3 rounded-full hover:bg-blue-900 transition-colors text-xl font-semibold"
                >
                  Back to Login
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
